import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';
import { getAllTeams } from '../../../services/teamService';

const ProblemStatementCard = ({ problem, isExpanded, onToggle, onApprove, onReject, isProcessing }) => {
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamsError, setTeamsError] = useState(null);

  useEffect(() => {
    if (showTeamDropdown && teams?.length === 0) {
      fetchTeams();
    }
  }, [showTeamDropdown]);

  const fetchTeams = async () => {
    try {
      setLoadingTeams(true);
      setTeamsError(null);
      const teamsData = await getAllTeams();
      setTeams(teamsData || []);
    } catch (err) {
      console.error('Error fetching teams:', err);
      setTeamsError(err?.message || 'Failed to load teams');
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleAcceptClick = () => {
    setShowTeamDropdown(true);
  };

  const handleConfirmAssignment = () => {
    if (!selectedTeamId) {
      alert('Please select a team');
      return;
    }
    onApprove(problem, selectedTeamId);
    setShowTeamDropdown(false);
    setSelectedTeamId('');
  };

  const handleCancelAssignment = () => {
    setShowTeamDropdown(false);
    setSelectedTeamId('');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      case 'approved':
        return 'bg-success/10 text-success border-success';
      case 'rejected':
        return 'bg-error/10 text-error border-error';
      default:
        return 'bg-muted/10 text-muted-foreground border-muted';
    }
  };

  const getComplexityColor = (complexity) => {
    switch (complexity) {
      case 'beginner':
        return 'bg-success/10 text-success';
      case 'intermediate':
        return 'bg-warning/10 text-warning';
      case 'advanced':
        return 'bg-error/10 text-error';
      default:
        return 'bg-muted/10 text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-soft">
      {/* Card Header - Always Visible */}
      <div
        className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-lg font-semibold text-foreground">{problem?.title}</h3>
              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(problem?.status)}`}>
                {problem?.status?.charAt(0)?.toUpperCase() + problem?.status?.slice(1)}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center">
                <Icon name="User" size={14} className="mr-1" />
                {problem?.submitter?.full_name || 'Unknown'}
              </span>
              <span className="flex items-center">
                <Icon name="Calendar" size={14} className="mr-1" />
                {formatDate(problem?.created_at)}
              </span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(problem?.status)}`}>
                {problem?.status?.charAt(0)?.toUpperCase() + problem?.status?.slice(1)}
              </span>
            </div>
          </div>
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={20}
            className="text-muted-foreground flex-shrink-0 ml-4"
          />
        </div>
      </div>
      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-6 border-t border-border bg-muted/20">
          <div className="space-y-6">
            {/* Submission Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="FileText" size={16} className="mr-2" />
                  Submission Details
                </h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Submitted by:</span>
                    <p className="font-medium text-foreground">{problem?.submitter?.full_name}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <p className="font-medium text-foreground">{problem?.submitter?.email}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Department:</span>
                    <p className="font-medium text-foreground">{problem?.submitter?.department || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">City:</span>
                    <p className="font-medium text-foreground">{problem?.submitter?.city || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Submission Date:</span>
                    <p className="font-medium text-foreground">{formatDate(problem?.created_at)}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-foreground mb-3 flex items-center">
                  <Icon name="Info" size={16} className="mr-2" />
                  Problem Details
                </h4>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Category:</span>
                    <p className="font-medium text-foreground">{problem?.category}</p>
                  </div>
                  {problem?.custom_category && (
                    <div>
                      <span className="text-sm text-muted-foreground">Custom Category:</span>
                      <p className="font-medium text-foreground">{problem?.custom_category}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm text-muted-foreground">Priority:</span>
                    <p className="font-medium text-foreground capitalize">{problem?.priority}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Complexity:</span>
                    <p className="font-medium text-foreground capitalize">{problem?.complexity}</p>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Status:</span>
                    <p className="font-medium text-foreground capitalize">{problem?.status}</p>
                  </div>
                  {problem?.assigned_team && (
                    <div>
                      <span className="text-sm text-muted-foreground">Assigned Team:</span>
                      <p className="font-medium text-foreground">
                        Team {problem?.assigned_team?.team_number} - {problem?.assigned_team?.team_name}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h4 className="font-semibold text-foreground mb-2 flex items-center">
                <Icon name="AlignLeft" size={16} className="mr-2" />
                Description
              </h4>
              <p className="text-foreground whitespace-pre-wrap">{problem?.description}</p>
            </div>

            {/* Background */}
            {problem?.background && (
              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center">
                  <Icon name="BookOpen" size={16} className="mr-2" />
                  Background
                </h4>
                <p className="text-foreground whitespace-pre-wrap">{problem?.background}</p>
              </div>
            )}

            {/* Expected Deliverables */}
            {problem?.expected_deliverables?.length > 0 && (
              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center">
                  <Icon name="CheckSquare" size={16} className="mr-2" />
                  Expected Deliverables
                </h4>
                <ul className="list-disc list-inside space-y-1">
                  {problem?.expected_deliverables?.map((deliverable, index) => (
                    <li key={index} className="text-foreground">{deliverable}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Timeline */}
            {(problem?.project_start_date || problem?.project_end_date || problem?.project_duration) && (
              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center">
                  <Icon name="Clock" size={16} className="mr-2" />
                  Timeline
                </h4>
                <div className="space-y-1">
                  {problem?.project_duration && (
                    <p className="text-foreground">
                      <span className="text-muted-foreground">Duration:</span> {problem?.project_duration}
                    </p>
                  )}
                  {problem?.project_start_date && (
                    <p className="text-foreground">
                      <span className="text-muted-foreground">Start Date:</span> {formatDate(problem?.project_start_date)}
                    </p>
                  )}
                  {problem?.project_end_date && (
                    <p className="text-foreground">
                      <span className="text-muted-foreground">End Date:</span> {formatDate(problem?.project_end_date)}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Stakeholders */}
            {problem?.stakeholders?.length > 0 && (
              <div>
                <h4 className="font-semibold text-foreground mb-2 flex items-center">
                  <Icon name="Users" size={16} className="mr-2" />
                  Stakeholders
                </h4>
                <div className="space-y-2">
                  {problem?.stakeholders?.map((stakeholder, index) => (
                    <div key={index} className="bg-background p-3 rounded-lg">
                      <p className="font-medium text-foreground">{stakeholder?.name}</p>
                      <p className="text-sm text-muted-foreground">{stakeholder?.role}</p>
                      {stakeholder?.contact && (
                        <p className="text-sm text-muted-foreground">{stakeholder?.contact}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            {problem?.status === 'pending' && (
              <div className="pt-4 border-t border-border space-y-4">
                {!showTeamDropdown ? (
                  <div className="flex items-center space-x-3">
                    <Button
                      variant="success"
                      iconName="Check"
                      iconPosition="left"
                      onClick={handleAcceptClick}
                      disabled={isProcessing}
                    >
                      Accept
                    </Button>
                    <Button
                      variant="danger"
                      iconName="X"
                      iconPosition="left"
                      onClick={() => onReject(problem)}
                      disabled={isProcessing}
                      loading={isProcessing}
                    >
                      Reject
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Assign Team <span className="text-error">*</span>
                      </label>
                      {loadingTeams ? (
                        <div className="flex items-center justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                      ) : teamsError ? (
                        <div className="bg-error/10 border border-error text-error px-3 py-2 rounded-lg text-sm">
                          {teamsError}
                        </div>
                      ) : teams?.length === 0 ? (
                        <div className="bg-muted/50 p-4 rounded-lg text-center text-muted-foreground text-sm">
                          No teams available
                        </div>
                      ) : (
                        <select
                          value={selectedTeamId}
                          onChange={(e) => setSelectedTeamId(e?.target?.value)}
                          className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Select a team...</option>
                          {teams?.map((team) => (
                            <option key={team?.id} value={team?.id}>
                              Team {team?.teamNumber} - {team?.teamName} ({team?.currentMembers || 0} members)
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="success"
                        iconName="Check"
                        iconPosition="left"
                        onClick={handleConfirmAssignment}
                        disabled={!selectedTeamId || isProcessing || loadingTeams}
                        loading={isProcessing}
                      >
                        Confirm Assignment
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleCancelAssignment}
                        disabled={isProcessing}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProblemStatementCard;